import type { FollowChangeItem } from "@/components/peeqd/tracking/types";

export type PeriodKind = "baseline" | "weekly" | "pending";

export interface DashboardWeekTab {
  id: string;
  label: string;
  startKey: string;
  endKey: string;
  kind: PeriodKind;
  isCurrent: boolean;
  /** Future week - analysis still being compiled. */
  isPending?: boolean;
  /** e.g. "Monday, 13 Jul 2026" */
  availableFromLabel?: string;
  /** Baseline only - when the first full weekly report lands. */
  nextReportLabel?: string;
}

const SCHEDULE_TZ = "America/Chicago";

function toChicagoDateKey(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHEDULE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function addDaysToDateKey(key: string, days: number): string {
  const [y, m, day] = key.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, day + days, 12, 0, 0));
  return toChicagoDateKey(utc);
}

function weekdayChicago(d: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: SCHEDULE_TZ,
    weekday: "short",
  }).format(d);
}

/** Monday of the week containing `d`, in America/Chicago (YYYY-MM-DD). */
export function mondayOfWeekChicago(d: Date): string {
  let cursor = new Date(d);
  for (let i = 0; i < 7; i++) {
    if (weekdayChicago(cursor) === "Mon") {
      return toChicagoDateKey(cursor);
    }
    cursor = new Date(cursor.getTime() - 86_400_000);
  }
  return toChicagoDateKey(d);
}

/** Sunday of the week containing `dateKey`. */
function sundayOfWeek(dateKey: string): string {
  let cursor = new Date(`${dateKey}T12:00:00Z`);
  for (let i = 0; i < 7; i++) {
    if (weekdayChicago(cursor) === "Sun") {
      return toChicagoDateKey(cursor);
    }
    cursor = new Date(cursor.getTime() + 86_400_000);
  }
  return dateKey;
}

function formatRangeLabel(startKey: string, endKey: string): string {
  const dateFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: SCHEDULE_TZ,
    month: "short",
    day: "numeric",
  });
  const yearFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: SCHEDULE_TZ,
    year: "numeric",
  });

  const start = new Date(`${startKey}T12:00:00Z`);
  const end = new Date(`${endKey}T12:00:00Z`);

  return `${dateFmt.format(start)} – ${dateFmt.format(end)}, ${yearFmt.format(end)}`;
}

function nextMondayAfter(dateKey: string): string {
  const sunday = sundayOfWeek(dateKey);
  return addDaysToDateKey(sunday, 1);
}

function formatMondayAvailable(mondayKey: string): string {
  const monday = new Date(`${mondayKey}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: SCHEDULE_TZ,
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(monday);
}

function buildBaselineTab(createdAt: string): DashboardWeekTab {
  const baselineEnd = toChicagoDateKey(new Date(createdAt));
  const baselineStart = addDaysToDateKey(baselineEnd, -6);
  const firstWeeklyMonday = nextMondayAfter(baselineEnd);

  return {
    id: `baseline-${baselineStart}`,
    label: formatRangeLabel(baselineStart, baselineEnd),
    startKey: baselineStart,
    endKey: baselineEnd,
    kind: "baseline",
    isCurrent: false,
    nextReportLabel: formatMondayAvailable(firstWeeklyMonday),
  };
}

function buildWeeklyPeriods(
  createdAt: string,
  todayKey: string
): { completed: DashboardWeekTab[]; pending: DashboardWeekTab | null } {
  const signupKey = toChicagoDateKey(new Date(createdAt));
  let periodStart = signupKey;
  let periodEnd = sundayOfWeek(signupKey);
  const completed: DashboardWeekTab[] = [];

  while (todayKey >= periodEnd) {
    completed.push({
      id: `weekly-${periodStart}`,
      label: formatRangeLabel(periodStart, periodEnd),
      startKey: periodStart,
      endKey: periodEnd,
      kind: "weekly",
      isCurrent: false,
    });
    periodStart = addDaysToDateKey(periodEnd, 1);
    periodEnd = addDaysToDateKey(periodStart, 6);
  }

  if (todayKey >= periodEnd) {
    return { completed, pending: null };
  }

  return {
    completed,
    pending: {
      id: `weekly-${periodStart}`,
      label: formatRangeLabel(periodStart, periodEnd),
      startKey: periodStart,
      endKey: periodEnd,
      kind: "pending",
      isCurrent: false,
      isPending: true,
      availableFromLabel: formatMondayAvailable(addDaysToDateKey(periodEnd, 1)),
    },
  };
}

/**
 * Build scrollable period tabs: baseline snapshot, completed weekly reports,
 * and one pending tab for the in-progress weekly period.
 */
export function buildDashboardWeekTabs(
  _changelog: FollowChangeItem[],
  lastSuccessAt?: string | null,
  createdAt?: string | null
): DashboardWeekTab[] {
  const now = new Date();
  const todayKey = toChicagoDateKey(now);
  const tabs: DashboardWeekTab[] = [];

  if (createdAt) {
    tabs.push(buildBaselineTab(createdAt));
    if (lastSuccessAt) {
      const { completed, pending } = buildWeeklyPeriods(createdAt, todayKey);
      tabs.push(...completed);
      if (pending) tabs.push(pending);
    }
  }

  return tabs;
}

export function filterChangelogForWeek(
  changelog: FollowChangeItem[],
  week: DashboardWeekTab
): FollowChangeItem[] {
  return changelog.filter((item) => {
    const key = toChicagoDateKey(new Date(item.detectedAt));
    return key >= week.startKey && key <= week.endKey;
  });
}

export function defaultWeekTabId(tabs: DashboardWeekTab[]): string {
  const baseline = tabs.find((t) => t.kind === "baseline");
  if (baseline) return baseline.id;

  const weekly = tabs.filter((t) => t.kind === "weekly");
  if (weekly.length > 0) return weekly.at(-1)!.id;

  return tabs[0]?.id ?? "";
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export interface ChangesSummaryData {
  weekLabel: string;
  username: string;
  total: number;
  gainedFollowers: number;
  lostFollowers: number;
  gainedFollowing: number;
  lostFollowing: number;
  netFollowers: number;
  empty: boolean;
}

export function buildChangesSummaryData(
  changelog: FollowChangeItem[],
  weekLabel: string,
  username: string
): ChangesSummaryData {
  const gainedFollowers = changelog.filter(
    (c) => c.side === "followers" && c.changeType === "follow"
  ).length;
  const lostFollowers = changelog.filter(
    (c) => c.side === "followers" && c.changeType === "unfollow"
  ).length;
  const gainedFollowing = changelog.filter(
    (c) => c.side === "following" && c.changeType === "follow"
  ).length;
  const lostFollowing = changelog.filter(
    (c) => c.side === "following" && c.changeType === "unfollow"
  ).length;

  return {
    weekLabel,
    username,
    total: changelog.length,
    gainedFollowers,
    lostFollowers,
    gainedFollowing,
    lostFollowing,
    netFollowers: gainedFollowers - lostFollowers,
    empty: changelog.length === 0,
  };
}

/** Plain-text summary for the selected week. */
export function buildChangesSummary(
  changelog: FollowChangeItem[],
  weekLabel: string,
  username: string
): string {
  const data = buildChangesSummaryData(changelog, weekLabel, username);

  if (data.empty) {
    return `No follower or following changes for @${username} between ${weekLabel}.`;
  }

  const sentences: string[] = [
    `Between ${weekLabel}, @${username} had ${data.total} ${plural(data.total, "change", "changes")}.`,
  ];

  const followerParts: string[] = [];
  if (data.gainedFollowers > 0) {
    followerParts.push(
      `${data.gainedFollowers} new ${plural(data.gainedFollowers, "follower", "followers")}`
    );
  }
  if (data.lostFollowers > 0) {
    followerParts.push(
      `${data.lostFollowers} ${plural(data.lostFollowers, "unfollower", "unfollowers")}`
    );
  }
  if (followerParts.length > 0) {
    sentences.push(`On their followers list: ${followerParts.join(" and ")}.`);
  }

  const followingParts: string[] = [];
  if (data.gainedFollowing > 0) {
    followingParts.push(
      `followed ${data.gainedFollowing} ${plural(data.gainedFollowing, "account", "accounts")}`
    );
  }
  if (data.lostFollowing > 0) {
    followingParts.push(
      `unfollowed ${data.lostFollowing} ${plural(data.lostFollowing, "account", "accounts")}`
    );
  }
  if (followingParts.length > 0) {
    sentences.push(`On accounts they follow: ${followingParts.join(" and ")}.`);
  }

  if (data.netFollowers !== 0) {
    sentences.push(
      `Net follower change: ${data.netFollowers > 0 ? "+" : ""}${data.netFollowers}.`
    );
  }

  return sentences.join(" ");
}

/** @deprecated Use mondayOfWeekChicago */
export const mondayOfWeekRiga = mondayOfWeekChicago;
